// Open-apps strip (VW-MIG-S1.D3 / CFR-VW-145) — the embedded-apps runtime's tab strip, RESTORE path.
//
// Renders the actor's OPEN apps as chips above the app content. Clicking a chip activates it and
// navigates to that app's mount route — which remounts the app and restores its last screen from
// AppSession (cat 48.16). NOT keep-alive: inactive apps are unmounted; their position is already
// DB-persisted, so nothing durable is lost (see state/openApps.ts for the architecture decision).
// Renders nothing when no apps are open, so it costs zero chrome in the common case.
import { useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Columns2, Maximize2, Minimize2, X } from "lucide-react";
import { clsx } from "clsx";
import { Tooltip, TooltipProvider } from "@perlab/ui";
import { getSubApp, ZARVIS_APP_ID } from "@perlab/registry";
import { moveRovingFocus } from "./rovingFocus";
import { openAppKey, useOpenApps, type OpenApp } from "../state/openApps";
import { useShellStore } from "../state/shellStore";
import { useContextChain } from "../state/ContextChain";
import { useAppInstances } from "../state/appInstances";
import { useAppCoherence } from "../state/useAppCoherence";
import { resolveAppTarget } from "./appTarget";
import { AppIconResolver } from "./AppIconResolver";
import { CoherenceBadge } from "./CoherenceBadge";
import { navTo } from "./navTargets";
import { SHELL_APP_RAIL_CLASS } from "./shellChrome";
import { AIDisclosureBadge } from "../inspector/AIDisclosureBadge";
import { resolveZarvisIdentity } from "../inspector/zarvisIdentity";

// Per-chip coherence signal (V2.04): resolve this open app's instance at its node, then read its
// stale worklist. Its OWN component so the hooks (one instances-fetch per node) stay rules-of-hooks
// safe across a variable chip list. Renders the amber "stale" badge, or nothing when fresh.
function ChipCoherence({ appId, scopeNodeId }: { appId: string; scopeNodeId: string }): React.ReactElement | null {
  const instances = useAppInstances(scopeNodeId);
  const inst = instances.kind === "ready" ? instances.data.find((i) => i.app_id === appId) : undefined;
  const { staleCount, stale } = useAppCoherence(inst?.id, scopeNodeId);
  return <CoherenceBadge staleCount={staleCount} stale={stale} />;
}

export function OpenAppsStrip(): React.ReactElement | null {
  const navigate = useNavigate();
  const stripRef = useRef<HTMLDivElement>(null);
  const open = useOpenApps((s) => s.open);
  const activeKey = useOpenApps((s) => s.activeKey);
  const activateApp = useOpenApps((s) => s.activateApp);
  const closeApp = useOpenApps((s) => s.closeApp);
  const appMax = useShellStore((s) => s.appMax);
  const toggleAppMax = useShellStore((s) => s.toggleAppMax);
  const { scopeNodes, workspaces } = useContextChain();
  // Split view lives in the URL (`?split=<app>`) → deep-linkable + refresh-safe.
  const search = useSearch({ strict: false }) as { split?: string };
  const splitAppId = search.split ?? null;
  const setSplit = (appId: string | null): void => {
    navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, split: appId ?? undefined }) } as never);
  };

  if (open.length === 0) return null;

  // Disambiguate chips of the SAME app open at DIFFERENT scopes (app@node identity): show the node
  // name so two "FormulatR" chips (e.g. Q4 vs FY25) are tellable apart.
  const appIdCounts = open.reduce<Record<string, number>>((acc, a) => {
    acc[a.appId] = (acc[a.appId] ?? 0) + 1;
    return acc;
  }, {});
  const zarvisActive = open.some(
    (app) => app.appId === ZARVIS_APP_ID && openAppKey(app) === activeKey,
  );
  const workspaceLabel = (workspaceId: string): string => (
    (workspaces ?? []).find((workspace) => workspace.id === workspaceId)?.label
    ?? scopeNodes.find((node) => (
      node.id === workspaceId && node.node_type === "workspace"
    ))?.name
    ?? workspaceId
  );
  const scopeSuffix = (a: { appId: string; workspaceId: string; scopeNodeId: string }): string | null =>
    a.appId !== ZARVIS_APP_ID && (appIdCounts[a.appId] ?? 0) > 1
      ? scopeNodes.find((n) => n.id === a.scopeNodeId)?.name ?? a.scopeNodeId
      : null;

  const goTo = (app: OpenApp): void => {
    navigate({
      ...resolveAppTarget(app),
      ...(app.sessionRoute ? { search: app.sessionRoute } : {}),
    } as never);
  };

  // `aria-owns` is a SPACE-SEPARATED id list, so an id has to survive being tokenised. An
  // open-app key is `<appId>@<nodeId>`; the sanitiser keeps it to `[A-Za-z0-9_-]`, and the
  // index prefix keeps two keys that sanitise alike from collapsing onto one id.
  const tabDomId = (key: string, index: number): string =>
    `open-app-tab-${index}-${key.replace(/[^A-Za-z0-9_-]/g, "_")}`;
  const tabIds = open.map((a, i) => tabDomId(openAppKey(a), i));

  return (
    <div
      ref={stripRef}
      data-app="open-apps-strip"
      // Roving tabindex (Wave 2 §2.2): Left/Right + Home/End move focus among the tab chips;
      // activation stays on click/Enter. Split/close buttons keep their own tab stops.
      onKeyDown={(e) => {
        if (moveRovingFocus(stripRef.current, e.key, '[role="tab"]', "horizontal")) e.preventDefault();
      }}
      className={`${SHELL_APP_RAIL_CLASS} flex items-center gap-1 overflow-x-auto px-3`}
    >
      {/* THE TABLIST OWNS THE TABS; IT DOES NOT CONTAIN THEM.
          `tablist` may only own `tab` elements. Each chip is a tab BESIDE its own split and
          close buttons, and the strip ends with the full-screen toggle — so a `role="tablist"`
          on this container owned the chip wrappers and that toggle, none of which is a tab.
          axe reports it as a CRITICAL `aria-required-children` failure, and Chromium's own
          accessibility tree bears it out: the tablist came back owning `generic, generic,
          button "Full screen app"` — no tabs at all, with each real tab buried inside a
          generic. With the owner element below it comes back owning exactly
          `tab "brainstorming", tab "dtv"`.

          Nesting a real tablist around the chips does not fix it: the split/close buttons are
          still inside. Making each chip the tab does not either — the buttons then sit inside
          an interactive element (`nested-interactive`). Both were measured, not assumed.

          `aria-owns` is the ARIA mechanism for exactly this: the accessibility tree gets a
          tablist whose children are the tabs and nothing else, while the DOM keeps each tab
          next to the controls that belong to its chip. The element is visually hidden rather
          than `display: contents` — an empty contents box has a history of being pruned from
          the accessibility tree, and a pruned tablist would leave every tab an orphan. */}
      <span role="tablist" aria-label="Open apps" aria-owns={tabIds.join(" ")} className="sr-only" />
      {open.map((a, index) => {
        const key = openAppKey(a);
        const active = key === activeKey;
        const description = getSubApp(a.appId)?.description;
        const workspaceName = workspaceLabel(a.workspaceId);
        const zarvisIdentity = resolveZarvisIdentity(workspaceName);
        const displayName = a.appId === ZARVIS_APP_ID
          ? zarvisIdentity.displayName
          : a.name;
        const tooltipDescription = a.appId === ZARVIS_APP_ID
          ? description
              ?.replace(/\bZarvis\b/g, displayName)
              .replace("permitted workspace", `the ${workspaceName} tower`)
          : description;
        return (
          <div
            key={key}
            className={clsx(
              "flex h-8 shrink-0 items-center gap-1 rounded-[2px] border-l-2 pl-1.5 pr-0.5 transition-colors",
              active
                ? "border-[var(--color-orange-55)] bg-[var(--color-surface-base)] text-[var(--color-text-primary)]"
                : "border-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-subtle)] hover:text-[var(--color-text-primary)]",
            )}
          >
            <TooltipProvider>
              <Tooltip
                content={tooltipDescription ?? displayName}
                disabled={!tooltipDescription}
                side="bottom"
                align="start"
              >
                <button
                  type="button"
                  role="tab"
                  id={tabDomId(key, index)}
                  aria-label={a.appId === ZARVIS_APP_ID
                    ? `${displayName}, ${workspaceName} tower AI assistant`
                    : undefined}
                  aria-selected={active}
                  aria-controls="shell-app-panel"
                  tabIndex={active ? 0 : -1}
                  onClick={() => {
                    activateApp(key);
                    goTo(a);
                  }}
                  className="flex items-center gap-1.5 py-1 text-[13px]"
                >
                  {a.appId === ZARVIS_APP_ID ? (
                    <span className="grid size-6 shrink-0 place-items-center overflow-hidden rounded-full border border-[var(--color-accent)]/60 bg-[var(--color-accent)]/[0.12]">
                      <img
                        src={zarvisIdentity.avatarUrl}
                        alt=""
                        data-testid="zarvis-tab-avatar"
                        className="size-full object-cover"
                      />
                    </span>
                  ) : (
                    <AppIconResolver name={a.icon} />
                  )}
                  <span className="max-w-[160px] truncate">{displayName}</span>
                  {a.appId === ZARVIS_APP_ID && (
                    <AIDisclosureBadge staged={false} variant="icon" />
                  )}
                  {scopeSuffix(a) && (
                    <span className="max-w-[120px] shrink-0 truncate text-[10px] text-[var(--color-text-secondary)]">
                      · {scopeSuffix(a)}
                    </span>
                  )}
                </button>
              </Tooltip>
            </TooltipProvider>
            {a.appId !== ZARVIS_APP_ID && (
              <ChipCoherence appId={a.appId} scopeNodeId={a.scopeNodeId} />
            )}
            {!zarvisActive && !active && a.appId !== ZARVIS_APP_ID && (
              <button
                type="button"
                aria-label={splitAppId === a.appId ? `Close split with ${displayName}` : `Open ${displayName} in split view`}
                aria-pressed={splitAppId === a.appId}
                title={splitAppId === a.appId ? "Close split view" : "Open in split view (same scope)"}
                onClick={() => setSplit(splitAppId === a.appId ? null : a.appId)}
                className="rounded-[2px] p-0.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
              >
                <Columns2 size={12} aria-hidden />
              </button>
            )}
            <button
              type="button"
              aria-label={a.appId === ZARVIS_APP_ID
                ? `Close ${displayName} in ${workspaceName}`
                : `Close ${displayName}`}
              onClick={() => {
                if (a.appId === splitAppId) setSplit(null);  // closing the split target ends the split
                closeApp(key);
                // If we just closed the ACTIVE app, follow the store's fallback to the new active app
                // (read fresh state after the set) so the content column doesn't strand on the closed one.
                if (active) {
                  const s = useOpenApps.getState();
                  const next = s.activeKey
                    ? s.open.find((x) => openAppKey(x) === s.activeKey)
                    : undefined;
                  if (next) {
                    goTo(next);
                  } else {
                    navigate(navTo.nodeHome(a.workspaceId, a.scopeNodeId) as never);
                  }
                }
                // Predictable focus after close (§2.2): once React commits the removal, land
                // focus on the now-selected tab so keyboard users aren't dropped to <body>.
                requestAnimationFrame(() => {
                  stripRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus();
                });
              }}
              className="rounded-[2px] p-0.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
            >
              <X size={13} aria-hidden />
            </button>
          </div>
        );
      })}
      {/* Max toggle — near-full-screen the active app (hides header + sidebar; Esc also exits). The
          strip only renders while an app is open (so one is always active); AppShell additionally
          gates the maxed view on an active app, so Max never leaves you chromeless with no exit. */}
      <button
        type="button"
        aria-label={appMax ? "Exit full screen" : "Full screen app"}
        aria-pressed={appMax}
        title={appMax ? "Exit full screen (Esc)" : "Full screen app"}
        onClick={toggleAppMax}
        className="ml-auto shrink-0 rounded-[2px] p-1 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
      >
        {appMax ? <Minimize2 size={14} aria-hidden /> : <Maximize2 size={14} aria-hidden />}
      </button>
    </div>
  );
}
